// src/pages/api/api.js
import axios from "axios";

/**
 * Instância da API
 * - Em dev: /api → é redirecionado pelo proxy (localhost:5000)
 * - Em prod: /api → é redirecionado pelo Nginx (porta 5000 no VPS)
 */
const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

// =====================================================================
// Autenticação JWT — enviar o token em cada pedido à API
// =====================================================================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================================================
// Sessão expirada ou inválida — tratamento global de HTTP 401
// =====================================================================
let redirecionamentoLoginEmCurso = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "").toLowerCase();

    const endpointPublico = url.includes("auth/login") || url.includes("auth/resetpassword");

    if (status === 401 && !endpointPublico && !redirecionamentoLoginEmCurso) {
      redirecionamentoLoginEmCurso = true;

      localStorage.removeItem("token");
      localStorage.removeItem("userData");

      // A navegação completa reinicializa também o AuthContext.
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }

    // Sessão expirada ou inválida:
    // o redirecionamento para login já foi iniciado acima.
    // Não propagar o 401 aos componentes, evitando alertas
    // técnicos e múltiplas mensagens durante a navegação.
    if (status === 401 && !endpointPublico) {
      // O redirecionamento já está a ser tratado globalmente.
      // Identificar o erro para evitar alertas nos helpers.
      error.sessaoExpirada = true;
    }

    return Promise.reject(error);
  }
);

/**
 * Helper padrão para lidar com o envelope { success, data, message }
 * e também com respostas "nuas" (array/objeto).
 * Devolve sempre: [data, err, meta]
 */
export async function handleApi(promise) {
  try {
    const res = await promise;
    const payload = res?.data;

    if (
      payload &&
      typeof payload === "object" &&
      Object.prototype.hasOwnProperty.call(payload, "success")
    ) {
      const success = !!payload.success;
      const message = payload.message ?? null;
      const severity = payload.severity ?? (success ? "success" : "error");
      return [payload.data ?? null, null, { success, message, severity }];
    }

    return [payload ?? null, null, { success: true, message: null, severity: "success" }];
  } catch (e) {
    // O interceptor já iniciou o redirecionamento para login.
    // Não devolver o HTTP 401 como erro normal da aplicação.
    if (e?.sessaoExpirada) {
      return [null, null, { success: false, sessaoExpirada: true }];
    }
    const message =
      e?.response?.data?.message ||
      e?.response?.data?.Message ||
      e?.response?.data ||
      e?.message ||
      "Erro de comunicação";
    return [null, message, { success: false, message, severity: "error" }];
  }
}

export default api;
